import {
  faInfoCircle,
  faPlus,
  faSearch,
  faSync,
  faTrash,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";

import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Button,
  ListGroup,
  ListGroupItem,
  Input,
  InputGroupText,
  InputGroup,
} from "reactstrap";

import ConfirmationModal from "../../../components/ConfirmationModal";
import InformationModal from "../../../components/InformationModal";
import Loader from "../../../components/Loader";
import { useAuth } from "../../../providers/authProvider";
import { notificationApi } from "../../../services/notificationServices";
import AddNotificationEmailModal from "../../../components/admin/AddNotificationEmailModal";
import TooltipItem from "../../../components/TooltipItem";
import AdvanceTablePagination from "../../../components/advanceTable/AdvanceTablePagination";

const INIT_PAGE_SIZE = 10;
const INIT_PAGE = 1;

const initialState = {
  sizePerPage: INIT_PAGE_SIZE,
  page: INIT_PAGE,
  search: "",
  sortBy: "email",
  direction: "asc",
};

const EmailNotifications = () => {
  const [authContext] = useAuth();
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [initState, setInitState] = useState(initialState);
  const [emailData, setEmailData] = useState({
    data: [],
    count: 0,
    totalPages: 0,
  });

  const initConfirmationModal = {
    isOpen: false,
    onSubmit: null,
    onClose: null,
    title: "",
    body: "",
  };

  const [confirmationModal, setConfirmationModal] = useState(
    initConfirmationModal
  );

  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await notificationApi.getEmailNotifications({
          serviceLocationId: authContext.serviceLocationId,
          pageSize: initState.sizePerPage,
          page: initState.page - 1,
          search: initState.search,
          sortBy: initState.sortBy,
          direction: initState.direction,
        });
        setEmailData(result);
        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    };
    fetchData();
  }, [
    refresh,
    authContext.serviceLocationId,
    initState.page,
    initState.sizePerPage,
    initState.search,
    initState.sortBy,
    initState.direction,
  ]);

  const handleSort = (field) => {
    setInitState((prev) => ({
      ...prev,
      sortBy: field,
      direction:
        prev.sortBy === field && prev.direction === "asc" ? "desc" : "asc",
      page: INIT_PAGE,
    }));
  };

  const getSortIcon = (field) => {
    if (initState.sortBy !== field) {
      return <FontAwesomeIcon icon={faSort} className="ml-1 text-muted" />;
    }
    return (
      <FontAwesomeIcon
        icon={initState.direction === "asc" ? faSortUp : faSortDown}
        className="ml-1 text-primary"
      />
    );
  };

  const removeEmail = (emailNotification) => {
    setConfirmationModal({
      isOpen: true,
      confirmColor: "danger",
      onSubmit: async () => {
        try {
          await notificationApi.deleteEmailNotification(emailNotification);

          // If we're deleting the last item on the current page (except page 1)
          if (emailData.data.length === 1 && initState.page > 1) {
            setInitState((prev) => ({
              ...prev,
              page: prev.page - 1, // Go back one page
            }));
          }

          setConfirmationModal(initConfirmationModal);
          setInformationModal({
            isOpen: true,
            title: "Remove Email Notification",
            body: "Email removed successfully.",
            onClose: () => {
              setRefresh(!refresh);
              setInformationModal({ isOpen: false, title: "", body: "" });
            },
          });
        } catch (err) {
          setConfirmationModal(initConfirmationModal);
          setLoading(false);
        }
      },
      onClose: () => {
        setConfirmationModal(initConfirmationModal);
      },
      title: "Remove Email Notification",
      body: `<span class="text-center">Do you confirm you want to remove ${emailNotification.email}?</span>`,
    });
  };

  return (
    <Container fluid className="flex-grow-1 flex-column d-flex">
      <Card>
        <CardHeader className="d-flex align-items-center justify-content-between mt-2">
          <div className="text-dark flex-grow-1 d-flex align-items-center">
            <h3 className="mb-0 ">
              Email Notifications{" "}
              <TooltipItem
                position="bottom"
                className="ml-1"
                id="payroll-tooltip"
                title="An email will be sent when a Job is completed"
              >
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="ml-1 text-primary"
                />
              </TooltipItem>
            </h3>
          </div>
          <div className="d-flex align-items-center justify-content-between">
            <InputGroup size="m" className="mr-3">
              <Input
                className="border-right-0"
                maxLength="50"
                placeholder="Search for.."
                value={initState.search}
                onChange={(evt) =>
                  setInitState({
                    ...initState,
                    search: evt.target.value,
                    page: INIT_PAGE,
                  })
                }
              />
              <InputGroupText className="search-input input-group-text bg-primary text-white border-left-0 border-primary">
                <FontAwesomeIcon icon={faSearch} />
              </InputGroupText>
            </InputGroup>
            <Button
              size="sm"
              className="mr-3 rounded-circle d-flex custom-rounded-button"
              color="primary"
              onClick={() => setRefresh(!refresh)}
            >
              <FontAwesomeIcon icon={faSync} />
            </Button>
            <Button
              size="sm"
              className="rounded-circle d-flex custom-rounded-button"
              color="primary"
              onClick={() => setAddModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} />
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Loader />
          ) : emailData.data.length ? (
            <ListGroup flush className="border-top">
              <ListGroupItem
                className="py-2 border-bottom font-weight-bold bg-lighter text-muted small d-flex justify-content-between"
                tag="div"
              >
                <div
                  className="col-5 d-flex align-items-center cursor-pointer"
                  onClick={() => handleSort("email")}
                  style={{ cursor: "pointer" }}
                >
                  Email {getSortIcon("email")}
                </div>
                <div
                  className="col-5 d-flex align-items-center cursor-pointer"
                  onClick={() => handleSort("serviceLocationName")}
                  style={{ cursor: "pointer" }}
                >
                  Service Location {getSortIcon("serviceLocationName")}
                </div>
                <div className="col-2"></div>
              </ListGroupItem>
              {emailData.data.map((emailNotification, index) => (
                <ListGroupItem
                  key={index}
                  className="py-2 border-bottom d-flex justify-content-between align-items-center"
                  tag="div"
                >
                  <div className="col-5">{emailNotification.email}</div>
                  <div className="col-5">
                    {emailNotification.serviceLocation?.name}
                  </div>
                  <div className="col-2 d-flex justify-content-end">
                    <Button
                      className="ml-2 rounded d-flex align-items-center"
                      color="none"
                      size="sm"
                      onClick={() => removeEmail(emailNotification)}
                    >
                      <FontAwesomeIcon
                        icon={faTrash}
                        className="text-danger"
                        size="lg"
                      />
                    </Button>
                  </div>
                </ListGroupItem>
              ))}
              <AdvanceTablePagination
                className="mt-3"
                totalCount={emailData.count}
                pageCount={emailData.totalPages}
                currentPage={initState.page - 1}
                onPageChange={(page) =>
                  setInitState({
                    ...initState,
                    page,
                  })
                }
                pageSize={initState.sizePerPage}
                onPageSizeChange={(sizePerPage) =>
                  setInitState({
                    ...initState,
                    sizePerPage,
                    page: INIT_PAGE,
                  })
                }
              />
            </ListGroup>
          ) : (
            <div className="text-center">No emails added</div>
          )}
        </CardBody>
      </Card>
      {addModal ? (
        <AddNotificationEmailModal
          submitFunction={notificationApi.createEmailNotification}
          onSubmit={() => {
            setAddModal(false);
            setInformationModal({
              isOpen: true,
              title: "Add Email Notification",
              body: "Email added successfully.",
              onClose: () => {
                setInformationModal({ isOpen: false, title: "", body: "" });
                setRefresh(!refresh);
              },
            });
          }}
          onClose={() => setAddModal(false)}
        />
      ) : confirmationModal.isOpen ? (
        <ConfirmationModal {...confirmationModal} />
      ) : informationModal.isOpen ? (
        <InformationModal
          title={informationModal.title}
          body={informationModal.body}
          onClose={() =>
            informationModal.onClose
              ? informationModal.onClose()
              : setInformationModal({ isOpen: false, title: "", body: "" })
          }
        />
      ) : null}
    </Container>
  );
};

export default EmailNotifications;
